import { computed, reactive, ref } from 'vue';
import { clamp } from 'lodash';
import { useHipSpineAnalysisViewModel } from '@/views/3d-planner/hipSpine/useHipSpineAnalysisViewModel';
import { useCupOverlayViewModel } from '@/views/3d-planner/cup/useCupOverlayViewModel';
import { usePlannerStore } from '@/views/3d-planner/store/plannerStore';
import { MAX_ANTEVERSION, MIN_ANTEVERSION } from '@/domain/anatomy/pelvis/anteversionInclination';

/**
 * Minimum raw (unrounded) degrees between the standing combined-anteversion target and the
 * current cup anteversion before the recommendation is worth prompting for. A raw difference
 * of exactly this value qualifies; anything smaller does not (inclusive boundary).
 */
const ANTEVERSION_DIFFERENCE_THRESHOLD_DEGREES = 1;

// The prompt lifecycle is a single module-scoped `isPromptOpen` ref shared by every caller —
// `modalActions` opens it, the modal reads it — so the state must be declared ONCE here, not
// created per call. All trigger/decision/apply logic lives on this module too; the modal and the
// host view are presentation-only.
//
// The three collaborators are resolved lazily inside each computed/function — never destructured
// at module scope — so this module has no construction-time dependency on them and each is
// independently stubbable in isolation.
const isPromptOpen = ref(false);

// True when the case uses combined ("auto") anteversion mode. Optional chaining keeps this
// false when no case (or specification/cup) is loaded, so nothing opens during load/poll.
const isCombinedMode = computed<boolean>(() => {
    const planner = usePlannerStore();
    return planner.case?.specification?.cup?.anteversion_mode === 'auto';
});

// True when the standing combined-anteversion target differs from the current cup
// anteversion by at least the threshold, comparing RAW (unrounded) values. Null on either
// side (target not yet computed, or cup anteversion absent) means no meaningful difference.
const meetsThreshold = computed<boolean>(() => {
    const analysis = useHipSpineAnalysisViewModel();
    const cup = useCupOverlayViewModel();
    const target = analysis.combinedVersionAtAnteversionAxis;
    const current = cup.anteversion;
    if (target === null || target === undefined) return false;
    if (current === null || current === undefined) return false;
    return Math.abs(target - current) >= ANTEVERSION_DIFFERENCE_THRESHOLD_DEGREES;
});

// Open the prompt immediately on the false→true approval edge (the user just approved the
// final pending posture) while in combined mode with a meaningful difference. The prompt
// surfaces stacked over the still-open X-ray review modal. Anything else leaves it closed.
function evaluateAfterApproval(wasAllApproved: boolean): void {
    const analysis = useHipSpineAnalysisViewModel();
    const isApprovalEdge = !wasAllApproved && analysis.isHipSpineApproved;
    if (isApprovalEdge && isCombinedMode.value && meetsThreshold.value) {
        isPromptOpen.value = true;
    }
}

// Reject: keep the supine-based recommendation. Changes no cup or analysis state; only
// closes the prompt.
function keepSupine(): void {
    isPromptOpen.value = false;
}

// Confirm: rebase the recommendation on standing. Enables the hip-spine view toggle and
// writes the rounded, clamped combined target to the cup anteversion (inclination
// untouched). If the target is null at confirm time, only closes the prompt.
function updateToStanding(): void {
    const analysis = useHipSpineAnalysisViewModel();
    const cup = useCupOverlayViewModel();
    const target = analysis.combinedVersionAtAnteversionAxis;
    if (target === null || target === undefined) {
        isPromptOpen.value = false;
        return;
    }
    analysis.isSafeZoneSliderMode = true;
    cup.anteversion = clamp(Math.round(target), MIN_ANTEVERSION, MAX_ANTEVERSION);
    isPromptOpen.value = false;
}

// The shared api is a single reactive facade over the module-scoped state, built once so every
// `useStandingAnteversionPromptViewModel()` call returns the SAME object observing the SAME
// `isPromptOpen`. `reactive` unwraps the ref/computeds on read and forwards writes to the ref,
// so consumers use plain value semantics (`promptVm.isPromptOpen = true`) exactly as before.
const api = reactive({
    isPromptOpen,
    isCombinedMode,
    meetsThreshold,
    evaluateAfterApproval,
    keepSupine,
    updateToStanding,
});

/**
 * Module-singleton composable for the standing-anteversion recommendation prompt. Returns the
 * shared reactive api; there is no per-call state.
 */
export function useStandingAnteversionPromptViewModel() {
    return api;
}
